/**
 * Volunteer Signup Page
 * Form for people to apply to volunteer with CatPals+
 */

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Heart, Users, Car, Home, CheckCircle, Clock, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const VolunteerPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    age_group: "",
    availability: [],
    interests: [],
    experience: "",
    why_volunteer: "",
    has_transport: false,
    can_foster: false,
    additional_info: ""
  });

  const availabilityOptions = [
    "Weekday mornings",
    "Weekday afternoons",
    "Weekday evenings",
    "Weekend mornings",
    "Weekend afternoons",
    "Weekend evenings"
  ];

  const interestOptions = [
    { id: "cat_care", label: "Cat Care & Socialisation", icon: "🐱" },
    { id: "fostering", label: "Fostering", icon: "🏠" },
    { id: "events", label: "Events & Fundraising", icon: "🎉" },
    { id: "admin", label: "Admin & Social Media", icon: "💻" },
    { id: "transport", label: "Cat Transport", icon: "🚗" },
    { id: "maintenance", label: "DIY & Maintenance", icon: "🔧" }
  ];

  const toggleAvailability = (option) => {
    setForm(prev => ({
      ...prev,
      availability: prev.availability.includes(option)
        ? prev.availability.filter(a => a !== option)
        : [...prev.availability, option]
    }));
  };

  const toggleInterest = (option) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.includes(option)
        ? prev.interests.filter(i => i !== option)
        : [...prev.interests, option]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.name || !form.email || !form.phone || !form.address || !form.age_group) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.availability.length === 0) {
      toast.error("Please select at least one availability option");
      return;
    }
    if (form.interests.length === 0) {
      toast.error("Please select at least one area of interest");
      return;
    }
    if (!form.experience || !form.why_volunteer) {
      toast.error("Please fill in the experience and motivation sections");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/volunteers`, form);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-36 bg-gradient-to-br from-green-50 via-stone-50 to-emerald-50" data-testid="volunteer-success">
        <div className="max-w-2xl mx-auto px-6 py-12 text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-4">Thank You!</h1>
          <p className="text-stone-600 text-lg mb-8">
            Your volunteer application has been submitted successfully. We'll review it and be in touch soon!
          </p>
          <Button onClick={() => window.location.href = "/"} className="bg-green-600 hover:bg-green-700 rounded-full">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-green-50 via-stone-50 to-emerald-50" data-testid="volunteer-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Become a Volunteer</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Join our amazing team of volunteers and help make a difference in the lives of rescue cats!
          </p>
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <Card className="bg-white/70 border-green-100">
            <CardContent className="p-6 text-center">
              <Heart className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-900 mb-2">Make a Difference</h3>
              <p className="text-sm text-stone-600">Help cats find their forever homes</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 border-green-100">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-900 mb-2">Gain Experience</h3>
              <p className="text-sm text-stone-600">Learn about cat care and rescue work</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 border-green-100">
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-stone-900 mb-2">Join Our Community</h3>
              <p className="text-sm text-stone-600">Meet fellow cat lovers</p>
            </CardContent>
          </Card>
        </div>

        {/* Application Form */}
        <Card className="bg-white shadow-lg">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">Volunteer Application</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-stone-900 border-b pb-2">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={form.name}
                      onChange={e => setForm({...form, name: e.target.value})}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => setForm({...form, email: e.target.value})}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={form.phone}
                      onChange={e => setForm({...form, phone: e.target.value})}
                      placeholder="Your phone number"
                      required
                    />
                  </div>
                  <div>
                    <Label>Age Group *</Label>
                    <Select value={form.age_group} onValueChange={v => setForm({...form, age_group: v})}>
                      <SelectTrigger><SelectValue placeholder="Select age group" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="18-25">18-25</SelectItem>
                        <SelectItem value="26-35">26-35</SelectItem>
                        <SelectItem value="36-50">36-50</SelectItem>
                        <SelectItem value="51-65">51-65</SelectItem>
                        <SelectItem value="65+">65+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Address *</Label>
                  <Textarea
                    value={form.address}
                    onChange={e => setForm({...form, address: e.target.value})}
                    placeholder="Your full address"
                    required
                  />
                </div>
              </div>

              {/* Availability */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-stone-900 border-b pb-2 flex items-center gap-2">
                  <Clock className="w-5 h-5" /> Availability *
                </h3>
                <p className="text-sm text-stone-500">Select all times you're typically available</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availabilityOptions.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleAvailability(option)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        form.availability.includes(option)
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-stone-200 bg-white text-stone-600 hover:border-green-300"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Areas of Interest */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-stone-900 border-b pb-2 flex items-center gap-2">
                  <Heart className="w-5 h-5" /> Areas of Interest *
                </h3>
                <p className="text-sm text-stone-500">What would you like to help with?</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {interestOptions.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleInterest(option.label)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        form.interests.includes(option.label)
                          ? "border-green-500 bg-green-50"
                          : "border-stone-200 bg-white hover:border-green-300"
                      }`}
                    >
                      <span className="text-2xl mb-2 block">{option.icon}</span>
                      <span className={`text-sm font-medium ${
                        form.interests.includes(option.label) ? "text-green-700" : "text-stone-700"
                      }`}>
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setForm({...form, has_transport: !form.has_transport})}
                  className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                    form.has_transport
                      ? "border-green-500 bg-green-50"
                      : "border-stone-200 bg-white hover:border-green-300"
                  }`}
                >
                  <Car className={`w-6 h-6 ${form.has_transport ? "text-green-600" : "text-stone-400"}`} />
                  <div className="text-left">
                    <p className={`font-medium ${form.has_transport ? "text-green-700" : "text-stone-700"}`}>
                      I have my own transport
                    </p>
                    <p className="text-xs text-stone-500">Can help with cat transport</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({...form, can_foster: !form.can_foster})}
                  className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                    form.can_foster
                      ? "border-green-500 bg-green-50"
                      : "border-stone-200 bg-white hover:border-green-300"
                  }`}
                >
                  <Home className={`w-6 h-6 ${form.can_foster ? "text-green-600" : "text-stone-400"}`} />
                  <div className="text-left">
                    <p className={`font-medium ${form.can_foster ? "text-green-700" : "text-stone-700"}`}>
                      I can foster cats
                    </p>
                    <p className="text-xs text-stone-500">Temporary home for cats in need</p>
                  </div>
                </button>
              </div>

              {/* Experience */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-stone-900 border-b pb-2">About You</h3>
                <div>
                  <Label>Previous Experience with Cats/Animals *</Label>
                  <Textarea
                    value={form.experience}
                    onChange={e => setForm({...form, experience: e.target.value})}
                    placeholder="Tell us about your experience with cats or other animals..."
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <Label>Why Do You Want to Volunteer? *</Label>
                  <Textarea
                    value={form.why_volunteer}
                    onChange={e => setForm({...form, why_volunteer: e.target.value})}
                    placeholder="What motivates you to volunteer with CatPals+?"
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <Label>Additional Information (Optional)</Label>
                  <Textarea
                    value={form.additional_info}
                    onChange={e => setForm({...form, additional_info: e.target.value})}
                    placeholder="Anything else you'd like us to know?"
                    rows={3}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg rounded-full"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VolunteerPage;
